import React, { useState, useEffect } from 'react';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, TrendingUp, ArrowRightLeft, DollarSign, Building2, User, Church, Calendar, MoreHorizontal } from 'lucide-react';
import { getIncome, createIncome, getTransfers, createTransfer, getIncomeSummary, getEvents } from '@/lib/api';
import { toast } from 'sonner';

const SOURCE_TYPES = [
  { value: 'Ministry/Organization', label: 'Ministry/Organization', icon: Building2 },
  { value: 'Individual Donor', label: 'Individual Donor', icon: User },
  { value: 'Church Offering', label: 'Church Offering', icon: Church },
  { value: 'Event Registration', label: 'Event Registration', icon: Calendar },
  { value: 'Other', label: 'Other', icon: MoreHorizontal }
];

const Income = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [showIncomeDialog, setShowIncomeDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Income form state
  const [incomeForm, setIncomeForm] = useState({
    source_name: '',
    source_type: 'Individual Donor',
    amount: '',
    currency: 'INR',
    purpose: '',
    payment_method: '',
    reference_number: ''
  });

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    event_id: '',
    amount: '',
    currency: 'INR',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [incomeRes, transfersRes, summaryRes, eventsRes] = await Promise.all([
        getIncome(),
        getTransfers(),
        getIncomeSummary(),
        getEvents()
      ]);
      setIncomeRecords(incomeRes.data);
      setTransfers(transfersRes.data);
      setSummary(summaryRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleIncomeSubmit = async (e) => {
    e.preventDefault();
    if (!incomeForm.source_name || !incomeForm.amount) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      await createIncome({
        ...incomeForm,
        amount: parseFloat(incomeForm.amount)
      });
      toast.success('Income recorded successfully!');
      setShowIncomeDialog(false);
      setIncomeForm({
        source_name: '',
        source_type: 'Individual Donor',
        amount: '',
        currency: 'INR',
        purpose: '',
        payment_method: '',
        reference_number: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to record income');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    if (!transferForm.event_id || !transferForm.amount) {
      toast.error('Please select an event and enter amount');
      return;
    }

    setSubmitting(true);
    try {
      await createTransfer({
        ...transferForm,
        amount: parseFloat(transferForm.amount)
      });
      toast.success('Transfer recorded successfully!');
      setShowTransferDialog(false);
      setTransferForm({
        event_id: '',
        amount: '',
        currency: 'INR',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to record transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency = 'INR') => {
    return currency === 'INR' ? `₹${amount.toLocaleString()}` : `$${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Income & Transfers
              </h2>
              <p className="text-sm text-muted-foreground">Track donations and fund allocations</p>
            </div>
            <div className="flex space-x-2">
              <Dialog open={showIncomeDialog} onOpenChange={setShowIncomeDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="add-income-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Income
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Record New Income</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleIncomeSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="source_name">Source Name *</Label>
                      <Input
                        id="source_name"
                        data-testid="income-source-name"
                        placeholder="e.g., John Doe, Grace Church"
                        value={incomeForm.source_name}
                        onChange={(e) => setIncomeForm({ ...incomeForm, source_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="source_type">Source Type</Label>
                      <Select 
                        value={incomeForm.source_type} 
                        onValueChange={(v) => setIncomeForm({ ...incomeForm, source_type: v })}
                      >
                        <SelectTrigger data-testid="income-source-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCE_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="amount">Amount *</Label>
                        <Input
                          id="amount"
                          type="number"
                          data-testid="income-amount"
                          placeholder="0.00"
                          value={incomeForm.amount}
                          onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="currency">Currency</Label>
                        <Select 
                          value={incomeForm.currency} 
                          onValueChange={(v) => setIncomeForm({ ...incomeForm, currency: v })}
                        >
                          <SelectTrigger data-testid="income-currency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INR">INR (₹)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="purpose">Purpose (optional)</Label>
                      <Input
                        id="purpose"
                        data-testid="income-purpose"
                        placeholder="e.g., For Chennai Revival Meeting"
                        value={incomeForm.purpose}
                        onChange={(e) => setIncomeForm({ ...incomeForm, purpose: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="payment_method">Payment Method</Label>
                        <Input
                          id="payment_method"
                          data-testid="income-payment-method"
                          placeholder="e.g., Bank Transfer"
                          value={incomeForm.payment_method}
                          onChange={(e) => setIncomeForm({ ...incomeForm, payment_method: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="reference_number">Reference No.</Label>
                        <Input
                          id="reference_number"
                          data-testid="income-reference"
                          placeholder="e.g., TXN123456"
                          value={incomeForm.reference_number}
                          onChange={(e) => setIncomeForm({ ...incomeForm, reference_number: e.target.value })}
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-income">
                      {submitting ? 'Recording...' : 'Record Income'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-transfer-button">
                    <ArrowRightLeft className="h-4 w-4 mr-2" />
                    Transfer Funds
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Transfer Funds to Event</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTransferSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="event_id">Select Event *</Label>
                      <Select 
                        value={transferForm.event_id} 
                        onValueChange={(v) => setTransferForm({ ...transferForm, event_id: v })}
                      >
                        <SelectTrigger data-testid="transfer-event">
                          <SelectValue placeholder="Choose an event" />
                        </SelectTrigger>
                        <SelectContent>
                          {events.map((event) => (
                            <SelectItem key={event.event_id} value={event.event_id}>
                              {event.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="transfer_amount">Amount *</Label>
                        <Input
                          id="transfer_amount"
                          type="number"
                          data-testid="transfer-amount"
                          placeholder="0.00"
                          value={transferForm.amount}
                          onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="transfer_currency">Currency</Label>
                        <Select 
                          value={transferForm.currency} 
                          onValueChange={(v) => setTransferForm({ ...transferForm, currency: v })}
                        >
                          <SelectTrigger data-testid="transfer-currency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INR">INR (₹)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        data-testid="transfer-notes"
                        placeholder="e.g., Initial allocation for venue booking"
                        value={transferForm.notes}
                        onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-transfer">
                      {submitting ? 'Transferring...' : 'Transfer Funds'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="max-w-7xl mx-auto space-y-6">
              {/* Summary Cards */}
              {summary && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-600 font-medium mb-1">Total Income</p>
                          <p className="text-3xl font-bold text-green-700">
                            ₹{summary.total_income_inr?.toLocaleString() || 0}
                          </p>
                          {summary.total_income_usd > 0 && (
                            <p className="text-sm text-green-600 mt-1">+ ${summary.total_income_usd?.toLocaleString()}</p>
                          )}
                        </div>
                        <TrendingUp className="h-10 w-10 text-green-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium mb-1">Allocated to Events</p>
                          <p className="text-3xl font-bold text-blue-700">
                            ₹{summary.total_transferred_inr?.toLocaleString() || 0}
                          </p>
                          {summary.total_transferred_usd > 0 && (
                            <p className="text-sm text-blue-600 mt-1">+ ${summary.total_transferred_usd?.toLocaleString()}</p>
                          )}
                        </div>
                        <ArrowRightLeft className="h-10 w-10 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-600 font-medium mb-1">Unallocated</p>
                          <p className="text-3xl font-bold text-amber-700">
                            ₹{summary.unallocated_inr?.toLocaleString() || 0}
                          </p>
                          {summary.unallocated_usd > 0 && (
                            <p className="text-sm text-amber-600 mt-1">+ ${summary.unallocated_usd?.toLocaleString()}</p>
                          )}
                        </div>
                        <DollarSign className="h-10 w-10 text-amber-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Tabs for Income and Transfers */}
              <Tabs defaultValue="income" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="income" data-testid="income-tab">
                    Income Records ({incomeRecords.length})
                  </TabsTrigger>
                  <TabsTrigger value="transfers" data-testid="transfers-tab">
                    Fund Transfers ({transfers.length})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="income" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Income Records</CardTitle>
                      <CardDescription>All donations and funds received</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {incomeRecords.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No income recorded yet</p>
                          <p className="text-sm">Click "Add Income" to record your first donation</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {incomeRecords.map((record) => {
                            const SourceIcon = SOURCE_TYPES.find(t => t.value === record.source_type)?.icon || MoreHorizontal;
                            return (
                              <div 
                                key={record.income_id} 
                                className="flex items-center justify-between p-4 bg-accent/20 rounded-lg"
                                data-testid={`income-record-${record.income_id}`}
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="p-2 bg-green-100 rounded-full">
                                    <SourceIcon className="h-5 w-5 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium">{record.source_name}</p>
                                    <p className="text-sm text-muted-foreground">
                                      {record.source_type} • {formatDate(record.created_at)}
                                    </p>
                                    {record.purpose && (
                                      <p className="text-xs text-muted-foreground mt-1">{record.purpose}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-green-600">
                                    {formatCurrency(record.amount, record.currency)}
                                  </p>
                                  {record.reference_number && (
                                    <p className="text-xs text-muted-foreground">Ref: {record.reference_number}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
                
                <TabsContent value="transfers" className="mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Fund Transfers</CardTitle>
                      <CardDescription>Allocations to events</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {transfers.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>No transfers recorded yet</p>
                          <p className="text-sm">Click "Transfer Funds" to allocate funds to an event</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {transfers.map((transfer) => (
                            <div 
                              key={transfer.transfer_id} 
                              className="flex items-center justify-between p-4 bg-accent/20 rounded-lg"
                              data-testid={`transfer-record-${transfer.transfer_id}`}
                            >
                              <div className="flex items-center space-x-4">
                                <div className="p-2 bg-blue-100 rounded-full">
                                  <ArrowRightLeft className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{transfer.event_name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    By {transfer.transferred_by_name} • {formatDate(transfer.created_at)}
                                  </p>
                                  {transfer.notes && (
                                    <p className="text-xs text-muted-foreground mt-1">{transfer.notes}</p>
                                  )}
                                </div>
                              </div>
                              <p className="text-lg font-bold text-blue-600">
                                {formatCurrency(transfer.amount, transfer.currency)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>

              {/* Breakdowns */}
              {summary && (summary.by_source?.length > 0 || summary.by_event?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {summary.by_source?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Income by Source Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {summary.by_source.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                              <span className="font-medium">{item.source_type}</span>
                              <span className="font-bold text-green-600">₹{item.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {summary.by_event?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Funds by Event</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {summary.by_event.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                              <span className="font-medium">{item.event_name}</span>
                              <span className="font-bold text-blue-600">₹{item.amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default Income;
